// frontend/components/PaymentMethodSelector.js
import React, { useState, useEffect, useRef } from "react";
import mollieService from "../services/mollie";

// Styles personnalisés restent inchangés
const styles = {
  paymentMethodCard: {
    border: "1px solid #e9ecef",
    borderRadius: "0.5rem",
    padding: "0.75rem",
    marginBottom: "0.5rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
  },
  paymentMethodCardSelected: {
    borderColor: "#543ba4",
    backgroundColor: "#f3f0ff",
    boxShadow: "0 0.125rem 0.25rem rgba(84, 59, 164, 0.1)",
  },
  paymentMethodImage: {
    width: "40px",
    height: "25px",
    objectFit: "contain",
    marginRight: "0.75rem",
  },
  loadingSpinner: {
    color: "#543ba4",
    width: "1.5rem",
    height: "1.5rem",
  },
  issuerSelect: {
    marginTop: "0.5rem",
    maxWidth: "100%",
  },
};

// Méthodes de paiement mockées pour le fallback
const FALLBACK_METHODS = [
  {
    id: "card",
    name: "Carte de crédit",
    image:
      "https://www.mollie.com/external/icons/payment-methods/creditcard.png",
    issuers: [],
  },
  {
    id: "bancontact",
    name: "Bancontact",
    image:
      "https://www.mollie.com/external/icons/payment-methods/bancontact.png",
    issuers: [],
  },
  {
    id: "paypal",
    name: "PayPal",
    image: "https://www.mollie.com/external/icons/payment-methods/paypal.png",
    issuers: [],
  },
];

/**
 * Composant pour sélectionner une méthode de paiement
 */
const PaymentMethodSelector = ({ onSelect, options = {} }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedIssuer, setSelectedIssuer] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  // Charger les méthodes de paiement
  useEffect(() => {
    // Empêcher les requêtes multiples
    if (hasFetchedRef.current) return;

    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        hasFetchedRef.current = true;

        console.log(
          "Chargement des méthodes de paiement avec options:",
          options
        );

        // Récupération des méthodes
        const methods = await mollieService.getPaymentMethods(options);

        // Vérification des méthodes
        if (!methods || !Array.isArray(methods) || methods.length === 0) {
          console.warn(
            "Pas de méthodes valides reçues, utilisation de méthodes de secours"
          );
          setPaymentMethods(FALLBACK_METHODS);
          setError(
            "Certaines méthodes de paiement pourraient ne pas être disponibles."
          );
        } else {
          console.log("Méthodes obtenues:", methods);
          setPaymentMethods(methods);
          setError(null);

          // Si une seule méthode est disponible, la sélectionner par défaut
          if (methods.length === 1) {
            handleMethodSelect(methods[0]);
          }
        }
      } catch (err) {
        console.error(
          "Erreur inattendue lors du chargement des méthodes:",
          err
        );
        setPaymentMethods(FALLBACK_METHODS);
        setError(
          "Certaines méthodes de paiement pourraient ne pas être disponibles."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, []); // Dépendances vides pour exécuter une seule fois au montage

  // Gérer la sélection d'une méthode de paiement
  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setSelectedIssuer("");

    // Si la méthode a des émetteurs et qu'aucun n'est sélectionné, ne pas appeler onSelect
    if (method.issuers && method.issuers.length > 0) {
      // Ne rien faire, attendre la sélection d'un émetteur
    } else {
      // Sinon, appeler onSelect avec la méthode sélectionnée
      onSelect({
        method: method.id,
        issuer: null,
      });
    }
  };

  // Gérer la sélection d'un émetteur
  const handleIssuerSelect = (e) => {
    const issuerId = e.target.value;
    setSelectedIssuer(issuerId);

    if (issuerId) {
      onSelect({
        method: selectedMethod.id,
        issuer: issuerId,
      });
    }
  };

  // Afficher le chargement
  if (loading) {
    return (
      <div className="text-center py-4">
        <div
          className="spinner-border"
          style={styles.loadingSpinner}
          role="status"
        >
          <span className="visually-hidden">
            Chargement des méthodes de paiement...
          </span>
        </div>
        <p className="mt-2">Chargement des méthodes de paiement...</p>
      </div>
    );
  }

  return (
    <div className="payment-method-selector">
      <h6 className="mb-3">Sélectionnez votre méthode de paiement</h6>

      {/* Afficher un message d'avertissement si erreur */}
      {error && (
        <div className="alert alert-info mb-3 py-2">
          <i className="bi bi-info-circle me-2"></i>
          <small>{error}</small>
        </div>
      )}

      {/* Liste des méthodes de paiement */}
      <div className="payment-methods-list">
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            style={{
              ...styles.paymentMethodCard,
              ...(selectedMethod && selectedMethod.id === method.id
                ? styles.paymentMethodCardSelected
                : {}),
            }}
            onClick={() => handleMethodSelect(method)}
          >
            <div className="form-check d-flex align-items-center flex-grow-1">
              <input
                type="radio"
                className="form-check-input"
                id={`method-${method.id}`}
                checked={selectedMethod && selectedMethod.id === method.id}
                onChange={() => handleMethodSelect(method)}
              />
              <label
                className="form-check-label ms-2 d-flex align-items-center flex-grow-1"
                htmlFor={`method-${method.id}`}
              >
                {method.image && (
                  <img
                    src={method.image}
                    alt={method.name}
                    style={styles.paymentMethodImage}
                  />
                )}
                <span>{method.name}</span>
              </label>
            </div>
          </div>
        ))}
      </div>

      {/* Sélecteur d'émetteur si nécessaire */}
      {selectedMethod &&
        selectedMethod.issuers &&
        selectedMethod.issuers.length > 0 && (
          <div className="mt-3">
            <label htmlFor="issuer-select" className="form-label">
              Sélectionnez votre banque
            </label>
            <select
              id="issuer-select"
              className="form-select"
              value={selectedIssuer}
              onChange={handleIssuerSelect}
              style={styles.issuerSelect}
            >
              <option value="">Choisissez votre banque</option>
              {selectedMethod.issuers.map((issuer) => (
                <option key={issuer.id} value={issuer.id}>
                  {issuer.name}
                </option>
              ))}
            </select>
          </div>
        )}
    </div>
  );
};

export default PaymentMethodSelector;
